// src/components/bot/questions/InteractiveButtonQuestion.tsx
// 🎨 INTERACTIVE BUTTON QUESTION - Enhanced UX with Real WhatsApp Preview
// ✨ IMPROVED: Better experience, visual previews, and intuitive editing

import React, { useState } from 'react';
import BaseQuestion from './BaseQuestion';
import styles from './Questions.module.css';
import { QuestionItem } from '../hooks/useBotForm';
import logger from '../../../utils/logger';

interface InteractiveButtonQuestionProps {
  question: QuestionItem;
  index: number;
  onUpdate: (id: string, field: keyof QuestionItem, value: any) => void;
  onRemove: (id: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  forceCollapsed?: boolean;
}

const InteractiveButtonQuestion: React.FC<InteractiveButtonQuestionProps> = (props) => {
  const { question, onUpdate } = props;
  
  logger.log('🔘 InteractiveButtonQuestion: Rendering question:', question.id, question.interactive);
  
  // 🔄 SYNC UPDATE HANDLER: Keep question.question and interactive.body.text in sync
  const handleUpdate = (id: string, field: keyof QuestionItem, value: any) => {
    if (field === 'question') {
      // When question text is updated, also update interactive body text
      const updatedInteractive = {
        ...question.interactive,
        body: { text: value }
      };
      onUpdate(id, 'interactive', updatedInteractive);
    }
    // Always call the original update
    onUpdate(id, field, value);
  };
  
  // 🎨 EDIT STATES: Better UX with inline editing
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [editingButtonIndex, setEditingButtonIndex] = useState<number | null>(null);
  
  // 🔥 INTERACTIVE DATA: Default structure with proper initialization
  const interactive = question.interactive || {
    type: 'button' as const,
    header: { type: 'text' as const, text: 'Choose an option' },
    body: { text: question.question || 'Please select one of the options below' },
    action: { 
      buttons: [
        { 
          type: 'reply', 
          reply: { id: `btn_${Date.now()}_1`, title: 'Yes' }
        },
        { 
          type: 'reply', 
          reply: { id: `btn_${Date.now()}_2`, title: 'No' }
        }
      ] 
    }
  };

  // 🔥 UPDATE INTERACTIVE: Helper function to update interactive data
  const updateInteractive = (field: string, value: any) => {
    logger.log('🔄 InteractiveButtonQuestion: Updating interactive field:', field, value);
    const updatedInteractive = { ...interactive, [field]: value };
    onUpdate(question.id, 'interactive', updatedInteractive);
  };

  // 🔥 UPDATE ACTION: Helper function to update action data
  const updateAction = (field: string, value: any) => {
    logger.log('🔄 InteractiveButtonQuestion: Updating action field:', field, value);
    const updatedAction = { ...interactive.action, [field]: value };
    updateInteractive('action', updatedAction);
  };

  // 🔥 ADD BUTTON: Add new button to the list
  const addButton = () => {
    logger.log('➕ InteractiveButtonQuestion: Adding new button');
    const currentButtons = interactive.action?.buttons || [];
    if (currentButtons.length >= 3) return; // WhatsApp limit
    
    const newButton = {
      type: 'reply',
      reply: {
        id: `btn_${Date.now()}_${currentButtons.length + 1}`,
        title: `Option ${currentButtons.length + 1}`
      }
    };
    
    updateAction('buttons', [...currentButtons, newButton]);
  };

  // 🔥 REMOVE BUTTON: Remove button from the list
  const removeButton = (buttonIndex: number) => {
    logger.log('🗑️ InteractiveButtonQuestion: Removing button:', buttonIndex);
    const currentButtons = interactive.action?.buttons || [];
    const updatedButtons = currentButtons.filter((_, index) => index !== buttonIndex);
    updateAction('buttons', updatedButtons);
  };

  // 🔥 UPDATE BUTTON: Modify button data
  const updateButton = (buttonIndex: number, field: string, value: any) => {
    logger.log('📝 InteractiveButtonQuestion: Updating button:', buttonIndex, field, value);
    const updatedButtons = [...(interactive.action?.buttons || [])];
    updatedButtons[buttonIndex] = {
      ...updatedButtons[buttonIndex],
      reply: {
        ...updatedButtons[buttonIndex].reply,
        [field]: value
      }
    };
    updateAction('buttons', updatedButtons);
  };

  // 🔥 HEADER MANAGEMENT
  const handleHeaderSave = () => {
    setIsEditingHeader(false);
  };

  // 🔥 BUTTON MANAGEMENT
  const handleButtonSave = () => {
    setEditingButtonIndex(null);
  };

  // 🎨 GET STATS: For better UX feedback
  const getButtonStats = () => {
    const buttons = interactive.action?.buttons || [];
    const avgLength = buttons.length > 0 
      ? Math.round(buttons.reduce((sum, btn) => sum + btn.reply.title.length, 0) / buttons.length)
      : 0;
    return { count: buttons.length, avgLength };
  };

  const stats = getButtonStats();

  return (
    <BaseQuestion {...props} onUpdate={handleUpdate}>
      
      {/* 🎯 INTERACTIVE BUTTON OPTIONS */}
      <div className={styles.typeSpecificOptions}>
        
        {/* 🎨 ENHANCED WHATSAPP PREVIEW - Prominent placement */}
        <div className={styles.whatsappPreviewSection}>
          <div className={styles.previewHeader}>
            <span className={styles.previewLabel}>📱 WhatsApp Preview</span>
            <div className={styles.previewBadge}>🔘 Buttons</div>
          </div>
          
          {/* 🎨 WHATSAPP STYLED PREVIEW */}
          <div className={styles.whatsappMockup}>
            <div className={styles.whatsappHeader}>
              <div className={styles.whatsappAvatar}>🤖</div>
              <div className={styles.whatsappContact}>Your Bot</div>
              <div className={styles.whatsappTime}>12:34 PM</div>
            </div>
            
            <div className={styles.whatsappMessage}>
              {/* Header */}
              {interactive.header?.text && (
                <div className={styles.whatsappMessageHeader}>
                  🔘 {interactive.header.text}
                </div>
              )}
              
              {/* Body */}
              <div className={styles.whatsappMessageBody}>
                {interactive.body?.text || question.question || 'Please select one of the options below'}
                {question.required && <span className={styles.requiredIndicator}>*</span>}
              </div>
              
              {/* Buttons */}
              <div className={styles.whatsappButtons}>
                {(interactive.action?.buttons || []).map((button, idx) => (
                  <div key={idx} className={styles.whatsappButton}>
                    🔘 {button.reply.title}
                  </div>
                ))}
                
                {/* Empty State */}
                {(!interactive.action?.buttons || interactive.action.buttons.length === 0) && (
                  <div className={styles.whatsappButtonEmpty}>
                    <i>No buttons configured yet</i>
                  </div>
                )}
              </div>
              
              <div className={styles.whatsappTimestamp}>12:34 PM ✓✓</div>
            </div>
          </div>
          
          {/* 🎨 STATS DISPLAY */}
          <div className={styles.buttonStats}>
            <span className={styles.statItem}>🔘 {stats.count}/3 buttons</span>
            <span className={styles.statItem}>📏 ~{stats.avgLength} chars avg</span>
            <span className={styles.statItem}>
              {question.required ? '🔒 Required' : '📝 Optional'}
            </span>
          </div>
        </div>

        {/* 🔥 HEADER TEXT */}
        <div className={styles.optionRow}>
          <label className={styles.optionLabel}>Header text:</label>
          <div className={styles.inlineEditContainer}>
            {isEditingHeader ? (
              <div className={styles.inlineEditor}>
                <input 
                  type="text"
                  value={interactive.header?.text || ''}
                  onChange={(e) => updateInteractive('header', { type: 'text', text: e.target.value })}
                  placeholder="e.g., Choose an option"
                  className={styles.inlineInput}
                  autoFocus
                  maxLength={60}
                />
                <div className={styles.inlineActions}>
                  <button type="button" onClick={handleHeaderSave} className={styles.saveBtn}>✓</button>
                  <button type="button" onClick={() => setIsEditingHeader(false)} className={styles.cancelBtn}>✕</button>
                </div>
              </div>
            ) : (
              <div className={styles.inlineDisplay} onClick={() => setIsEditingHeader(true)}>
                {interactive.header?.text || <span className={styles.placeholder}>Click to add header</span>}
                <span className={styles.editIcon}>✏️</span>
              </div>
            )}
          </div>
          <small className={styles.helpText}>
            Short header text shown above the buttons
          </small>
        </div>

        {/* 🔥 BUTTONS MANAGEMENT */}
        <div className={styles.optionRow}>
          <label className={styles.optionLabel}>Buttons (max 3):</label>
          <div className={styles.buttonsContainer}>
            
            {/* 🎨 BUTTONS EDITOR */}
            <div className={styles.buttonsEditor}>
              {(interactive.action?.buttons || []).map((button, buttonIndex) => (
                <div key={buttonIndex} className={styles.buttonCard}>
                  <div className={styles.buttonHeader}>
                    <span className={styles.buttonNumber}>Button {buttonIndex + 1}</span>
                    <div className={styles.buttonActions}>
                      <span className={styles.charCount}>{button.reply.title.length}/20</span>
                      {(interactive.action?.buttons || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeButton(buttonIndex)}
                          className={styles.removeButtonBtn}
                          title="Remove button"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.buttonInputContainer}>
                    {editingButtonIndex === buttonIndex ? (
                      <div className={styles.inlineEditor}>
                        <input
                          type="text"
                          value={button.reply.title}
                          onChange={(e) => updateButton(buttonIndex, 'title', e.target.value)}
                          placeholder={`Button ${buttonIndex + 1} text`}
                          className={styles.inlineInput}
                          autoFocus
                          maxLength={20}
                        />
                        <div className={styles.inlineActions}>
                          <button type="button" onClick={handleButtonSave} className={styles.saveBtn}>✓</button>
                          <button type="button" onClick={() => setEditingButtonIndex(null)} className={styles.cancelBtn}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={styles.buttonDisplay} 
                        onClick={() => setEditingButtonIndex(buttonIndex)}
                      >
                        <span className={styles.buttonText}>🔘 {button.reply.title}</span>
                        <span className={styles.editIcon}>✏️</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Add Button */}
              {(interactive.action?.buttons || []).length < 3 && (
                <button
                  type="button"
                  onClick={addButton}
                  className={styles.addButtonBtn}
                >
                  ➕ Add Button ({(interactive.action?.buttons || []).length}/3)
                </button>
              )}
            </div>
            
          </div>
          <small className={styles.helpText}>
            WhatsApp supports up to 3 reply buttons. Keep text short and clear.
          </small>
        </div>
        
      </div>

      {/* 🎯 WHATSAPP INFO */}
      <div className={styles.whatsappInfo}>
        <div className={styles.infoHeader}>
          <i className="fab fa-whatsapp" style={{ color: '#25d366' }}></i>
          <span>WhatsApp Button Features</span>
        </div>
        <div className={styles.infoContent}>
          <div className={styles.infoItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>Maximum 3 buttons per message</span>
          </div>
          <div className={styles.infoItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>20 character limit per button</span>
          </div>
          <div className={styles.infoItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>Quick reply functionality</span>
          </div>
          <div className={styles.infoItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>User-friendly button interface</span>
          </div>
        </div>
      </div>
      
    </BaseQuestion>
  );
};

export default InteractiveButtonQuestion;